import { supabase } from "../../supabase/supabase";

const API_BASE_URL = import.meta.env.VITE_BACKEND_API_URL || "http://localhost:8000";

interface RunSimulationResponse {
  success: boolean;
  message: string;
  simulator_id: string;
  status: string;
}

interface SimulationStatus {
  simulator_id: string;
  status: string;
  transcript: any[];
  started_at: string | null;
  ended_at: string | null;
  configuration: any;
}

async function getAuthToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("Not authenticated");
  }
  return session.access_token;
}

export const simulationService = {
  /**
   * Run a simulation
   */
  async runSimulation(simulatorId: string, organizationId: string): Promise<RunSimulationResponse> {
    try {
      const token = await getAuthToken();

      const response = await fetch(`${API_BASE_URL}/api/simulation/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          simulator_id: simulatorId,
          organization_id: organizationId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to run simulation");
      }

      return await response.json();
    } catch (error) {
      console.error("Error running simulation:", error);
      throw error;
    }
  },

  /**
   * Get simulation status
   */
  async getSimulationStatus(simulatorId: string, organizationId: string): Promise<SimulationStatus> {
    try {
      const token = await getAuthToken();

      const response = await fetch(`${API_BASE_URL}/api/simulation/${simulatorId}/status?organization_id=${encodeURIComponent(organizationId)}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to get simulation status");
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting simulation status:", error);
      throw error;
    }
  },

  /**
   * Poll simulation status until completion
   */
  async pollSimulationStatus(
    simulatorId: string,
    organizationId: string,
    onUpdate: (status: SimulationStatus) => void,
    intervalMs: number = 2000,
    maxAttempts: number = 150
  ): Promise<void> {
    let attempts = 0;

    const poll = async () => {
      try {
        const status = await this.getSimulationStatus(simulatorId, organizationId);
        onUpdate(status);

        if (status.status === "completed" || status.status === "failed") {
          return; // Stop polling
        }

        attempts++;
        if (attempts >= maxAttempts) {
          throw new Error("Simulation timed out");
        }

        // Continue polling
        setTimeout(poll, intervalMs);
      } catch (error) {
        console.error("Error polling simulation status:", error);
        throw error;
      }
    };

    await poll();
  },
};